<script setup lang="ts">
const logo = useTemplateRef('logo')
const navigation = useTemplateRef('navigation')

const auth = useAuth()
const fast = useFast()
const { myLink } = useLinks()
export type WindowType = '' | 'menu' | 'search' | 'cart' | 'notifications'

const timer = useTimeout()
const init = useDelayInit(100)
const init2 = useDelayInit(200)
const init3 = useInited('init3')

type NavStat = { isFixed: boolean; isVisible: boolean; noAnimation?: boolean }

const data = shallowReactive({
  init: false,
  type: '' as WindowType,
  navState: { isVisible: true, isFixed: false } satisfies NavStat as NavStat,
})

function windowTypeSet(value: WindowType | 'close') {
  timer.clear()
  const el = helpers.getHTMLElement(logo.value)
  if (el && getFocusedElement() === el) el.blur?.()
  // if (value !== 'search') refs.searchInput.value?.blur()

  if (value === 'close' || !value || value === data.type) {
    // Off
    if (!value) return
    data.type = ''
    return
  }

  if (data.type === value) return
  if (!data.init) data.init = true
  timer.set(() => (data.type = value), data.type ? 40 : 200)
}

const isB2b = useIsB2B()
const route = useRoute()
watch(
  () => ({ name: route.name, params: route.params }),
  () => windowTypeSet('close')
)

function navStateSet(v: NavStat) {
  const p = data.navState
  if (p.isFixed !== v.isFixed || p.isVisible !== v.isVisible || p.noAnimation !== v.noAnimation) {
    data.navState = v
  }
}

const { scrollTop, scrollingDown } = useScroll()

watch(
  () => ({ isDown: scrollingDown.value, top: scrollTop.value, isOpened: !!data.type?.length }),
  ({ isDown, top, isOpened }) => {
    if (isOpened) return navStateSet({ isFixed: true, isVisible: true, noAnimation: true })
    const el = helpers.getHTMLElement(navigation.value)
    if (!el) return navStateSet({ isVisible: true, isFixed: false })
    const navTop = myElem(el, 'offsetTop', 0)
    if (!isDown) return navStateSet({ isFixed: navTop < top, isVisible: true })
    const navBottom = navTop + myElem(el, 'offsetHeight', 0)
    const isFixed = navBottom < top + 5
    return navStateSet({ isFixed, isVisible: !isFixed, noAnimation: top < navBottom + 10 })
  }
)

const navClass = computed(() => {
  return {
    init: !init.value,
    init2: !init2.value,
    navFixed: data.navState.isFixed,
    navVisible: data.navState.isVisible,
    navNoAnimation: data.navState.noAnimation,
  }
})
</script>

<template>
  <nav :class="[$style.nav, navClass]">
    <div :class="$style.navBg" />

    <div ref="navigation" :class="$style.navRight">
      <NuxtLink ref="logo" :to="myLink('index')" :class="[$style.logo1, !fast && '_mra']" :aria-label="`${SEO_NAME} logo`">
        <IconLogo class="_h40 _w40" />
      </NuxtLink>
      <NuxtLink v-if="fast" aria-label="Open B2B" class="_mra" :class="[$style.btn, isB2b ? 'on' : 'off']" :to="myLink('b2b')" @click="windowTypeSet('close')">
        <span class="_fw-xxl _fs09">B2B</span>
      </NuxtLink>

      <button type="button" aria-label="Open search" :class="[$style.btn, data.type === 'search' ? 'on' : 'off']" @click="windowTypeSet('search')">
        <IconSearch />
        <LazyAppNavigationMobileExit v-if="init3" :on="data.type === 'search'" />
      </button>
      <button v-if="auth.isAuth" type="button" aria-label="Open notifications" :class="[$style.btn, data.type === 'notifications' ? 'on' : 'off']" @click="windowTypeSet('notifications')">
        <AppNotifications />
        <LazyAppNavigationMobileExit v-if="init3" :on="data.type === 'notifications'" />
      </button>
      <button type="button" aria-label="Open cart" :class="[$style.btn, data.type === 'cart' ? 'on' : 'off']" @click="windowTypeSet('cart')">
        <CartIcon :count="auth.cartSize" :added-once="() => windowTypeSet('cart')" />
        <LazyAppNavigationMobileExit v-if="init3" :on="data.type === 'cart'" />
      </button>
      <button type="button" aria-label="Open menu" :class="[$style.btn, data.type === 'menu' ? 'on' : 'off']" @click="windowTypeSet('menu')">
        <IconBurger :active="data.type === 'menu'" />
      </button>
    </div>

    <LazyAppNavigationMobileWindow v-if="data.init" v-memo="[navigation, data.type]" :elem="navigation" :type="data.type" :exit="() => windowTypeSet('close')" />
  </nav>
</template>

<style module>
.bg {
  top: 0;
  height: var(--nav-h);
}
.nav:global(.init) .bg {
  opacity: 0.01;
}
.nav:global(.init2) .bg {
  transition: opacity 500ms var(--tran2);
}
.nav:global(.navFixed) .bg {
  position: fixed;
  transform: translateY(-100%);
  transition: transform 300ms ease;
}
.nav:global(.navFixed) .bg {
  opacity: 0.99;
}
.nav:global(.navVisible) .bg {
  transform: translateY(0);
}
.nav:global(.navNoAnimation) .bg {
  transition: none;
}
.navBg {
  composes: bg;
  composes: global(_fixed-start);
  composes: global(_w100p);
  composes: global(_bg0);

  z-index: 98;
  opacity: 0.001;
  transition: opacity 300ms ease;
}
.navRight {
  composes: global(_absolute);
  composes: global(_row);
  composes: global(_w100p);
  composes: bg;
  z-index: 100;
  left: 0;
}
.logo1 {
  width: var(--nav-h);
  height: var(--nav-h);
  composes: global(_ctr);
}
.logo1:focus,
.logo1:active {
  background: var(--blue3);
  color: var(--bg);
}

.btn {
  composes: global(_relative);
  composes: global(_ctr);
  composes: global(_a1);
  background: transparent;
  outline: none;
  border: none;
  height: var(--nav-h);
  width: var(--nav-h);
}
.btn:global(.on) {
  border-color: var(--nav-color);
  background: var(--blue3);
  color: var(--bg);
}
</style>
